import { type SyntheticEvent, useState } from "react";

import { cn, tv } from "../foundations/cn.js";
import { useSizedImage } from "../foundations/imageContext.js";
import { useImageFallback } from "../foundations/useImageFallback.js";
import { ArtPlaceholder } from "./ArtPlaceholder.js";

/** The slot a card reserves for its art: the same for every item, so a grid lines up. */
const frameStyles = tv({
  base: "relative flex items-center justify-center overflow-hidden",
  variants: {
    shape: {
      /** A trading card's slot, 63:88. */
      card: "aspect-[63/88]",
      /** A square slot: sealed product, figures, anything without a card's proportions. */
      square: "aspect-square",
    },
  },
  defaultVariants: { shape: "card" },
});

/**
 * The art's own corners, as a share of its own box so they stay round at any size: a card's
 * (4.5% of 63 wide is 3.2% of 88 tall), turned on its side for landscape art, and the same both
 * ways for square art. On art with a transparent background they cut nothing visible.
 */
const ART_CORNERS = {
  portrait: "rounded-[4.5%/3.2%]",
  landscape: "rounded-[3.2%/4.5%]",
  square: "rounded-[3.8%]",
} as const;

type ArtOrientation = keyof typeof ART_CORNERS;

/** Within a tenth of square counts as square: a sealed box photographed a little off true. */
const SQUARE_TOLERANCE = 0.1;

/** Which way a picture of this many pixels runs. */
function artOrientation(width: number, height: number): ArtOrientation {
  const ratio = width / height;
  if (ratio > 1 + SQUARE_TOLERANCE) return "landscape";
  if (ratio < 1 - SQUARE_TOLERANCE) return "portrait";
  return "square";
}

export interface ItemArtProps {
  src: string;
  /** The item's name; empty when the name is already written beside it. */
  alt: string;
  /** The slot the art sits in. The art keeps its own proportions inside it, whatever they are. */
  shape?: "card" | "square";
  /** Load it first: the one image a page leads with. */
  priority?: boolean;
  /**
   * The most CSS pixels wide it is drawn: the ImageProvider then fetches it at that size (and
   * twice it for sharp screens) rather than at its original size; a size that fails to load falls
   * back to the original. Leave out to load the original.
   */
  width?: number;
  /** On the frame: its width (the height follows the shape) and placement. */
  className?: string;
}

/**
 * An item's picture in its slot: contained at its own proportions, never cropped or stretched,
 * centered, with corners that follow the art rather than the slot. Portrait, landscape and square
 * art all go through here, so no card measures or adjusts art itself; the frame says which way
 * the art runs as `data-orientation` once it has loaded. A sized copy that fails falls back to the
 * original; an original that is missing or fails draws a card's back in the slot
 * (`ArtPlaceholder`), named as the art was.
 */
export function ItemArt({ src, alt, shape, priority = false, width, className }: ItemArtProps) {
  const sized = useSizedImage(src, width);
  const fallback = useImageFallback(src);
  // The original a sized copy failed for: shown in its place, until the item's picture changes.
  const [unsized, setUnsized] = useState<string | null>(null);
  // Which way the loaded picture runs, and for which src: a new picture starts as a card.
  const [measured, setMeasured] = useState<{ src: string; orientation: ArtOrientation } | null>(
    null,
  );
  const original = unsized === src;
  const resized = sized.src !== src || sized.srcSet !== undefined;
  const orientation = measured?.src === src ? measured.orientation : undefined;
  const onLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0)
      setMeasured({ src, orientation: artOrientation(naturalWidth, naturalHeight) });
  };
  if (fallback.failed) {
    return (
      <span className={frameStyles({ shape, className })}>
        <ArtPlaceholder kind="item" label={alt} />
      </span>
    );
  }
  return (
    <span data-orientation={orientation} className={frameStyles({ shape, className })}>
      <img
        src={original ? src : sized.src}
        srcSet={original ? undefined : sized.srcSet}
        onError={resized && !original ? () => setUnsized(src) : fallback.onError}
        onLoad={onLoad}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className={cn("block max-h-full max-w-full", ART_CORNERS[orientation ?? "portrait"])}
      />
    </span>
  );
}
